import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfMonth, endOfMonth, format } from "date-fns";
import { getDealSalesAmount } from "@/utils/salesAmount";
import { parseDateSafe, getLocalDayStart } from "@/lib/timezoneUtils";

export interface SalesReportFilters {
  startDate: Date;
  endDate: Date;
  businessUnit?: 'us' | 'br' | 'all';
  salesConsultantIds?: string[];
  sources?: string[];
  channels?: string[];
  paymentStatuses?: PaymentStatus[];
  contractStatuses?: ContractStatus[];
  cardStatuses?: CardStatus[];
  dealStatuses?: DealStatus[];
  autoChargeStatuses?: AutoChargeStatus[];
}

export type PaymentStatus = 'settled' | 'paid' | 'partial' | 'late' | 'cancelled' | 'no_payment';
export type ContractStatus = 'draft' | 'sent' | 'completed' | 'no_contract' | 'issues';
export type CardStatus = 'saved' | 'not_saved';
export type DealStatus = 'active' | 'cancelled';
export type AutoChargeStatus = 'enabled' | 'card_only' | 'no_card';


export interface SalesReportDeal {
  id: string;
  name: string;
  total_amount: number;
  effective_amount: number;
  closed_date: string;
  contact_name: string;
  sales_consultant_name: string | null;
  sdr_name: string | null;
  deals_source: string | null;
  deals_channel: string | null;
  payment_status: PaymentStatus;
  contract_status: ContractStatus;
  card_status: CardStatus;
  deal_status: DealStatus;
  auto_charge_status: AutoChargeStatus;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  amount_received: number;
  amount_scheduled: number;
}

export interface SalesReportSummary {
  totalSales: number;
  totalReceived: number;
  totalScheduled: number;
  dealsCount: number;
  averageTicket: number;
}

export function useSalesReport(filters: SalesReportFilters) {
  return useQuery({
    queryKey: ["sales-report", filters],
    queryFn: async () => {
      const startDateStr = format(filters.startDate, "yyyy-MM-dd");
      const endDateStr = format(filters.endDate, "yyyy-MM-dd");

      // Fetch WON deals with contact and user info
      let query = supabase
        .from("deals")
        .select(`
          id,
          name,
          total_amount,
          upsell_credit_amount,
          transferred_payments_amount,
          closed_date,
          deals_source,
          deals_channel,
          card_on_file,
          deal_status,
          cancelled_at,
          cancellation_reason,
          business_unit,
          contact:contacts!fk_deals_contact(first_name, last_name, stripe_default_payment_method_id, has_payment_method),
          sales_consultant:app_users!sales_consultant(first_name, last_name),
          sdr:app_users!sales_development(first_name, last_name),
          deal_contracts(status),
          payments(status, amount, paid_amount, due_date, auto_charge_enabled)
        `)
        .eq("stage", "won")
        .gte("closed_date", startDateStr)
        .lte("closed_date", endDateStr)
        .order("closed_date", { ascending: false });

      // Filtro de Business Unit
      if (filters.businessUnit && filters.businessUnit !== 'all') {
        query = query.eq("business_unit", filters.businessUnit);
      }

      if (filters.salesConsultantIds?.length) {
        query = query.in("sales_consultant", filters.salesConsultantIds);
      }

      if (filters.sources?.length) {
        query = query.in("deals_source", filters.sources);
      }

      if (filters.channels?.length) {
        query = query.in("deals_channel", filters.channels);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Transform data
      // Usar getLocalDayStart para evitar problemas de timezone
      const today = getLocalDayStart(new Date());

      const deals: SalesReportDeal[] = (data || []).map((deal: any) => {
        // Calculate payment status based on OVERDUE payments only
        const payments = deal.payments || [];
        let payment_status: PaymentStatus = 'no_payment';
        
        // Calculate amount received (sum of paid_amount from paid payments)
        const amount_received = payments
          .filter((p: any) => p.status === 'paid')
          .reduce((sum: number, p: any) => sum + (p.paid_amount || p.amount || 0), 0);
        
        // Parse endDate to compare with payment due dates
        const endDateParsed = parseDateSafe(endDateStr);
        const endDateForComparison = endDateParsed ? new Date(endDateParsed.getTime()) : new Date();
        endDateForComparison.setHours(23, 59, 59, 999);
        
        // Calculate amount scheduled (sum of unpaid payments due within the selected period)
        const amount_scheduled = payments
          .filter((p: any) => {
            if (p.status === 'paid') return false; // Already paid
            if (!p.due_date) return false; // No due date
            // Usar parseDateSafe para evitar bug de timezone com YYYY-MM-DD
            const dueDate = parseDateSafe(p.due_date);
            if (!dueDate) return false;
            const dueDateStart = getLocalDayStart(dueDate);
            // Due in future AND within selected period
            return dueDateStart > today && dueDateStart <= endDateForComparison;
          })
          .reduce((sum: number, p: any) => sum + (p.amount || 0), 0);
        
        if (payments.length > 0) {
          // Separate overdue payments (due_date <= today) from future ones
          const overduePayments = payments.filter((p: any) => {
            if (!p.due_date) return true; // No due date = consider as overdue
            // Usar parseDateSafe para evitar bug de timezone
            const dueDate = parseDateSafe(p.due_date);
            if (!dueDate) return true;
            const dueDateStart = getLocalDayStart(dueDate);
            return dueDateStart <= today;
          });

          const allPaid = payments.every((p: any) => p.status === 'paid');
          const allPaidOrCancelled = payments.every((p: any) => 
            p.status === 'paid' || p.status === 'cancelled'
          );
          
          if (allPaid) {
            // All payments (including future) are paid = SETTLED (Quitado)
            payment_status = 'settled';
          } else if (allPaidOrCancelled) {
            // Some paid, rest cancelled = CANCELLED
            payment_status = 'cancelled';
          } else if (overduePayments.length === 0) {
            // No overdue payments yet, only future ones - client is on track (Em dia)
            payment_status = 'paid';
          } else {
            // Filter only PENDING overdue payments (not paid and not cancelled)
            const pendingOverdue = overduePayments.filter((p: any) => 
              p.status !== 'paid' && p.status !== 'cancelled'
            );
            const paidOverdue = overduePayments.filter((p: any) => p.status === 'paid');
            
            if (pendingOverdue.length === 0) {
              // All overdue are either paid or cancelled
              payment_status = paidOverdue.length > 0 ? 'paid' : 'cancelled';
            } else if (paidOverdue.length > 0) {
              payment_status = 'partial'; // Some overdue paid, some pending = partial
            } else {
              payment_status = 'late'; // No overdue payments paid = late (Atrasado)
            }
          }
        }

        // Get contract status (latest one)
        const contracts = deal.deal_contracts || [];
        const contract_status: ContractStatus = contracts.length > 0 ? contracts[0].status : 'no_contract';

        // Calculate auto-charge status based on Stripe card and auto_charge_enabled payments
        const hasStripeCard = !!(deal.contact?.stripe_default_payment_method_id || deal.contact?.has_payment_method);
        const hasAutoChargePayments = payments.some((p: any) => p.auto_charge_enabled === true);
        const auto_charge_status: AutoChargeStatus = 
          hasStripeCard && hasAutoChargePayments ? 'enabled' :
          hasStripeCard ? 'card_only' : 'no_card';

        const dealData = {
          id: deal.id,
          name: deal.name || "Sem nome",
          total_amount: Number(deal.total_amount) || 0,
          upsell_credit_amount: Number(deal.upsell_credit_amount) || 0,
          closed_date: deal.closed_date,
          contact_name: deal.contact
            ? `${deal.contact.first_name || ""} ${deal.contact.last_name || ""}`.trim() || "Sem contato"
            : "Sem contato",
          sales_consultant_name: deal.sales_consultant
            ? `${deal.sales_consultant.first_name || ""} ${deal.sales_consultant.last_name || ""}`.trim() || null
            : null,
          sdr_name: deal.sdr
            ? `${deal.sdr.first_name || ""} ${deal.sdr.last_name || ""}`.trim() || null
            : null,
          deals_source: deal.deals_source,
          deals_channel: deal.deals_channel,
          payment_status,
          contract_status,
          card_status: (deal.card_on_file ? 'saved' : 'not_saved') as CardStatus,
          deal_status: (deal.deal_status === 'cancelled' ? 'cancelled' : 'active') as DealStatus,
          auto_charge_status,
          cancelled_at: deal.cancelled_at || null,
          cancellation_reason: deal.cancellation_reason || null,
          amount_received,
          amount_scheduled,
        };

        return {
          ...dealData,
          effective_amount: getDealSalesAmount(dealData),
        };
      });

      // Apply client-side filters for calculated statuses
      let filteredDeals = deals;
      if (filters.paymentStatuses?.length) {
        filteredDeals = filteredDeals.filter(d => filters.paymentStatuses!.includes(d.payment_status));
      }
      if (filters.contractStatuses?.length) {
        filteredDeals = filteredDeals.filter(d => filters.contractStatuses!.includes(d.contract_status));
      }
      if (filters.cardStatuses?.length) {
        filteredDeals = filteredDeals.filter(d => filters.cardStatuses!.includes(d.card_status));
      }
      if (filters.dealStatuses?.length) {
        filteredDeals = filteredDeals.filter(d => filters.dealStatuses!.includes(d.deal_status));
      }
      if (filters.autoChargeStatuses?.length) {
        filteredDeals = filteredDeals.filter(d => filters.autoChargeStatuses!.includes(d.auto_charge_status));
      }

      // Calculate summary
      // Regra de cancelamento por competência:
      // - Cancelado no mesmo mês do fechamento: conta apenas o recebido
      // - Cancelado em mês diferente: mantém o total (não altera histórico)
      const totalSales = filteredDeals.reduce((sum, deal) => sum + deal.effective_amount, 0);
      const totalReceived = filteredDeals.reduce((sum, deal) => sum + deal.amount_received, 0);
      const totalScheduled = filteredDeals.reduce((sum, deal) => sum + deal.amount_scheduled, 0);
      const dealsCount = filteredDeals.length;
      const averageTicket = dealsCount > 0 ? totalSales / dealsCount : 0;

      const summary: SalesReportSummary = {
        totalSales,
        totalReceived,
        totalScheduled,
        dealsCount,
        averageTicket,
      };

      return { deals: filteredDeals, summary };
    },
  });
}

export function useSalesFiltersOptions() {
  // Fetch unique sources and channels from deals
  const sourcesQuery = useQuery({
    queryKey: ["sales-report-sources"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("deals")
        .select("deals_source")
        .eq("stage", "won")
        .not("deals_source", "is", null);

      if (error) throw error;

      const uniqueSources = [...new Set((data || []).map((d) => d.deals_source).filter(Boolean))];
      return uniqueSources as string[];
    },
  });

  const channelsQuery = useQuery({
    queryKey: ["sales-report-channels"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("deals")
        .select("deals_channel")
        .eq("stage", "won")
        .not("deals_channel", "is", null);

      if (error) throw error;

      const uniqueChannels = [...new Set((data || []).map((d) => d.deals_channel).filter(Boolean))];
      return uniqueChannels as string[];
    },
  });

  const consultantsQuery = useQuery({
    queryKey: ["sales-report-consultants"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_users")
        .select("id, first_name, last_name")
        .eq("active", true)
        .order("first_name");

      if (error) throw error;
      return data || [];
    },
  });

  return {
    sources: sourcesQuery.data || [],
    channels: channelsQuery.data || [],
    consultants: consultantsQuery.data || [],
    isLoading: sourcesQuery.isLoading || channelsQuery.isLoading || consultantsQuery.isLoading,
  };
}
